import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref as rtdbRef, get as rtdbGet, update as rtdbUpdate } from 'firebase/database';
import { Button } from '../common/Button';
import { CapValueInput } from '../common/CapValueInput';
import { db, rtdb, uploadPublicAdminImage } from '../../services/firebase';
import { BannerPreview } from '../BrandBannerFallbacks/BannerPreview';
import { DeepLinkField } from '../common/DeepLinkField';
import {
  buildDeepLink,
  parseDeepLink,
  DEEP_LINK_NONE,
  type ParsedDeepLink,
} from '../Banners/deepLinkTargets';
import { checkCapValue, type CapValue } from '../../services/aiRateLimitConfig';
import { useIsRtdbAdmin } from '../../hooks/useIsRtdbAdmin';
import {
  DEFAULT_SUPPORTED_CURRENCIES,
  type ConfigSchema,
  type FieldOverlay,
  type FieldType,
  type ImageField,
  type ObjectField,
  type SchemaOverlay,
  type ValueSource,
} from './configSchema';
import { resolveSchemaOverlay } from './schemaOverlay';

/**
 * Generic, schema-driven panel for editing a single live-config document
 * (Firestore OR Realtime Database). Fetches the doc on mount, renders one
 * typed input per field, and saves the dirty fields back. Unedited keys on
 * the underlying doc are preserved (Firestore: merge; RTDB: update on the
 * parent node, which only touches the keys we pass).
 *
 * Supports scalar (boolean/number/string/select), masked (password),
 * string-array (chip list), readonly (display), nested object, and object-array
 * (repeater) fields, plus whole-panel and per-field read-only.
 *
 * A schema may also declare a `defaultsSource`, fetched alongside the document
 * and merged over the static field definitions — for config whose defaults,
 * ranges and applicability are owned by a backend and would drift if restated
 * here. Fields marked `requiresOverlay` hide themselves when that fetch fails,
 * so the panel never renders an input it cannot describe correctly.
 */

type Scalar = boolean | number | string | undefined;
type FieldValue =
  | Scalar
  | string[]
  | { [key: string]: FieldValue }
  | Array<{ [key: string]: FieldValue }>;

type FormState = Record<string, FieldValue>;

type LoadState = 'loading' | 'ready' | 'error';

interface SchemaConfigPanelProps {
  schema: ConfigSchema;
}

// ── CurrencyFreaks symbol source (public endpoint, no API key) ───────────────

const CF_CACHE_KEY = 'adminConfig:currencyFreaks:symbols';
const CF_TTL_MS = 24 * 60 * 60 * 1000; // daily

async function fetchCurrencyFreaksSymbols(): Promise<string[]> {
  try {
    const cached = localStorage.getItem(CF_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as { at: number; codes: string[] };
      if (
        parsed &&
        Array.isArray(parsed.codes) &&
        parsed.codes.length > 0 &&
        Date.now() - parsed.at < CF_TTL_MS
      ) {
        return parsed.codes;
      }
    }
  } catch {
    // ignore cache read errors
  }
  try {
    const res = await fetch('https://api.currencyfreaks.com/v2.0/currency-symbols');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { currencySymbols?: Record<string, string> };
    const codes = data.currencySymbols ? Object.keys(data.currencySymbols).sort() : [];
    if (codes.length > 0) {
      try {
        localStorage.setItem(CF_CACHE_KEY, JSON.stringify({ at: Date.now(), codes }));
      } catch {
        // ignore cache write errors
      }
      return codes;
    }
    return [...DEFAULT_SUPPORTED_CURRENCIES];
  } catch {
    return [...DEFAULT_SUPPORTED_CURRENCIES];
  }
}

function useValueSourceOptions(source?: ValueSource): {
  options: string[] | null;
  loading: boolean;
} {
  const [options, setOptions] = useState<string[] | null>(
    source?.kind === 'static' ? source.options : null,
  );
  const [loading, setLoading] = useState(source?.kind === 'currencyfreaks');

  useEffect(() => {
    let cancelled = false;
    if (!source) {
      setOptions(null);
      setLoading(false);
      return;
    }
    if (source.kind === 'static') {
      setOptions(source.options);
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetchCurrencyFreaksSymbols().then((codes) => {
      if (!cancelled) {
        setOptions(codes);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return { options, loading };
}

// ── Value helpers ────────────────────────────────────────────────────────────

function defaultValueFor(field: FieldType): FieldValue {
  switch (field.type) {
    case 'boolean':
      return field.defaultValue ?? false;
    case 'number':
      return field.defaultValue;
    case 'string':
    case 'password':
      return field.defaultValue ?? '';
    case 'select':
      return field.defaultValue ?? field.options[0]?.value ?? '';
    case 'string-array':
      return field.defaultValue ? [...field.defaultValue] : [];
    case 'readonly':
      return undefined;
    case 'cap':
      return field.defaultValue;
    case 'image':
      return { url: '', storagePath: '' };
    case 'date':
      return '';
    case 'deep-link':
      return deepLinkState(parseDeepLink(''));
    case 'object': {
      const out: Record<string, FieldValue> = {};
      for (const [k, sub] of Object.entries(field.fields)) out[k] = defaultValueFor(sub);
      return out;
    }
    case 'object-array':
      return [];
  }
}

/** `YYYY-MM-DD` and a real calendar date. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function isIsoDate(v: string): boolean {
  if (!ISO_DATE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/**
 * A deep-link field's form value: the parsed picker state, so a half-built link
 * (target chosen, id not picked yet) is representable and blocks Save instead
 * of being written. Flattened to strings so it fits `FieldValue`.
 */
function deepLinkState(parsed: ParsedDeepLink): Record<string, FieldValue> {
  return {
    mode: parsed.mode,
    id: parsed.id,
    customUrl: parsed.customUrl,
    ...(parsed.label ? { label: parsed.label } : {}),
    ...(parsed.param !== undefined ? { param: parsed.param } : {}),
  };
}
function toParsedDeepLink(value: FieldValue): ParsedDeepLink {
  const v = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<
    string,
    FieldValue
  >;
  const str = (x: FieldValue) => (typeof x === 'string' ? x : '');
  return {
    mode: str(v.mode) || DEEP_LINK_NONE,
    id: str(v.id),
    customUrl: str(v.customUrl),
    ...(str(v.label) ? { label: str(v.label) } : {}),
    ...(str(v.param) ? { param: str(v.param) } : {}),
  };
}

/** An image field's form value. */
function imageValue(value: FieldValue): { url: string; storagePath: string } {
  const v = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<
    string,
    FieldValue
  >;
  return {
    url: typeof v.url === 'string' ? v.url : '',
    storagePath: typeof v.storagePath === 'string' ? v.storagePath : '',
  };
}

/**
 * `parent` is the object the key was read from — the whole doc at top level, the
 * sub-object inside an object field. An image needs it: its Storage path is a
 * sibling key, not part of its own value.
 */
function coerceFromDoc(field: FieldType, raw: unknown, parent?: Record<string, unknown>): FieldValue {
  if (field.type === 'image') {
    const path = parent?.[field.storagePathKey];
    return {
      url: typeof raw === 'string' ? raw : '',
      storagePath: typeof path === 'string' ? path : '',
    };
  }
  if (raw === undefined || raw === null) {
    return field.type === 'readonly' ? undefined : defaultValueFor(field);
  }
  switch (field.type) {
    case 'boolean':
      return typeof raw === 'boolean' ? raw : defaultValueFor(field);
    case 'number': {
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw);
        if (Number.isFinite(n)) return n;
      }
      return defaultValueFor(field);
    }
    case 'string':
    case 'password':
      return typeof raw === 'string' ? raw : String(raw ?? '');
    case 'select':
      return typeof raw === 'string' ? raw : defaultValueFor(field);
    case 'readonly':
      return raw as FieldValue;
    case 'date':
      return typeof raw === 'string' && isIsoDate(raw) ? raw : '';
    case 'deep-link':
      return deepLinkState(parseDeepLink(typeof raw === 'string' ? raw : ''));
    case 'cap': {
      // Read the stored value the way watchlock reads it, so what the panel
      // shows is what the backend would act on — including the console-typed
      // 'Unlimited' and numeric strings that `resolveCap` accepts. A value it
      // would reject falls back to the shipped default here too, rather than
      // showing an input the backend is quietly ignoring.
      if (typeof raw === 'string') {
        if (raw.trim().toLowerCase() === 'unlimited') return 'unlimited';
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0) return Math.floor(n);
        return defaultValueFor(field);
      }
      if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
        return Math.floor(raw);
      }
      return defaultValueFor(field);
    }
    case 'string-array':
      return Array.isArray(raw)
        ? raw.filter((x): x is string => typeof x === 'string')
        : defaultValueFor(field);
    case 'object': {
      const obj =
        raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as Record<string, unknown>)
          : {};
      const out: Record<string, FieldValue> = {};
      for (const [k, sub] of Object.entries(field.fields)) out[k] = coerceFromDoc(sub, obj[k], obj);
      return out;
    }
    case 'object-array': {
      const arr = Array.isArray(raw) ? raw : [];
      return arr.map((item) => {
        const obj = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        const out: Record<string, FieldValue> = {};
        for (const [k, sub] of Object.entries(field.itemFields)) {
          out[k] = coerceFromDoc(sub, obj[k]);
        }
        return out;
      });
    }
  }
}

function normalizeRtdbPath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function parseFirestorePath(path: string): { collection: string; docId: string } {
  const parts = path.split('/').filter(Boolean);
  if (parts.length < 2 || parts.length % 2 !== 0) {
    throw new Error(`Invalid Firestore doc path: ${path}`);
  }
  return {
    collection: parts.slice(0, -1).join('/'),
    docId: parts[parts.length - 1],
  };
}

async function fetchConfig(schema: ConfigSchema): Promise<Record<string, unknown> | null> {
  if (schema.database === 'firestore') {
    const { collection, docId } = parseFirestorePath(schema.path);
    const snap = await getDoc(doc(db, collection, docId));
    return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
  }
  const snap = await rtdbGet(rtdbRef(rtdb, normalizeRtdbPath(schema.path)));
  if (!snap.exists()) return null;
  const val = snap.val();
  return val && typeof val === 'object' ? (val as Record<string, unknown>) : {};
}

async function writeConfig(schema: ConfigSchema, patch: Record<string, FieldValue>): Promise<void> {
  if (schema.database === 'firestore') {
    // Strip top-level undefined — Firestore rejects them outright.
    const clean: Record<string, FieldValue> = {};
    for (const [k, v] of Object.entries(patch)) {
      if (v !== undefined) clean[k] = v;
    }
    const { collection, docId } = parseFirestorePath(schema.path);
    await setDoc(doc(db, collection, docId), clean, { merge: true });
    return;
  }
  // RTDB: an emptied optional field must DELETE its key, not skip it — that is
  // what returns the value to a backend default that is still free to move,
  // whereas writing today's default would pin it forever. `null` is RTDB's
  // delete; `undefined` would throw. Only top-level keys are mapped, matching
  // what the patch actually carries — a `null` inside an array member would
  // shift the remaining indices rather than clear a field.
  const clean: Record<string, FieldValue | null> = {};
  for (const [k, v] of Object.entries(patch)) {
    clean[k] = v === undefined ? null : v;
  }
  // update() on the parent path only touches the listed keys — other sibling
  // keys on the node are preserved.
  await rtdbUpdate(rtdbRef(rtdb, normalizeRtdbPath(schema.path)), clean);
}

// ── Form value → stored shape ────────────────────────────────────────────────

/** The stored value of a field that writes a single key (everything but image). */
function storedScalar(field: FieldType, value: FieldValue): FieldValue {
  if (field.type === 'deep-link') {
    return buildDeepLink(toParsedDeepLink(value)).value ?? undefined;
  }
  if (field.type === 'date') return typeof value === 'string' && value ? value : undefined;
  if (field.type === 'object') return storedObject(field, value);
  return value;
}

function isBlank(v: FieldValue): boolean {
  return v === undefined || (typeof v === 'string' && v.trim() === '');
}

/**
 * An object field's sub-values as they are written. Images expand to their URL
 * plus the sibling path key; `omitBlank` drops empty optional values rather than
 * writing ''. Never contains `undefined` (RTDB rejects it inside an object).
 */
function storedObject(field: ObjectField, value: FieldValue): Record<string, FieldValue> {
  const obj = (value as Record<string, FieldValue> | undefined) ?? {};
  const out: Record<string, FieldValue> = {};
  const put = (k: string, v: FieldValue) => {
    if (v === undefined) return;
    if (field.omitBlank && isBlank(v)) return;
    out[k] = v;
  };
  for (const [k, sub] of Object.entries(field.fields)) {
    if (sub.type === 'image') {
      const img = imageValue(obj[k]);
      put(k, img.url.trim());
      put(sub.storagePathKey, img.url.trim() ? img.storagePath : '');
      continue;
    }
    put(k, storedScalar(sub, obj[k]));
  }
  return out;
}

/**
 * The top-level keys one dirty field writes. Usually one; an image writes its
 * URL and its path key together so the pair can never disagree. `undefined`
 * means "delete the key" (RTDB `null` in writeConfig).
 */
export function storedEntries(key: string, field: FieldType, value: FieldValue): Record<string, FieldValue> {
  if (field.type === 'image') {
    const img = imageValue(value);
    const url = img.url.trim();
    return { [key]: url || undefined, [field.storagePathKey]: url && img.storagePath ? img.storagePath : undefined };
  }
  return { [key]: storedScalar(field, value) };
}

// ── Overlay merge ────────────────────────────────────────────────────────────

/**
 * Merge one field's runtime metadata over its static definition. Only the
 * value-shaped properties are overridable — a backend can own what a default
 * is, but never what a field IS, so type, label and key stay local.
 */
function withOverlay(field: FieldType, overlay?: FieldOverlay): FieldType {
  if (!overlay) return field;
  const help = overlay.helpSuffix
    ? [field.help, overlay.helpSuffix].filter(Boolean).join(' ')
    : field.help;
  if (field.type === 'number') {
    return {
      ...field,
      help,
      placeholder: overlay.placeholder ?? field.placeholder,
      min: overlay.min ?? field.min,
      max: overlay.max ?? field.max,
      defaultValue:
        typeof overlay.defaultValue === 'number' ? overlay.defaultValue : field.defaultValue,
    };
  }
  if (field.type === 'boolean') {
    return {
      ...field,
      help,
      defaultValue:
        typeof overlay.defaultValue === 'boolean' ? overlay.defaultValue : field.defaultValue,
    };
  }
  return { ...field, help };
}

/**
 * The fields this panel actually renders, with any overlay applied.
 *
 * A `requiresOverlay` field is dropped whenever its metadata is missing — the
 * overlay failed, or resolved but marked the field inapplicable to this doc.
 * Dropping rather than disabling is deliberate: a visible input still asserts
 * that the setting exists, and for an inapplicable field that assertion is
 * simply false. Dropped fields are absent from validation, dirty tracking and
 * the save patch, so a hidden field can never be written.
 */
function effectiveFields(
  schema: ConfigSchema,
  overlay: SchemaOverlay | null,
): Array<[string, FieldType]> {
  const resolved = overlay?.status === 'ready' ? overlay.fields : null;
  const out: Array<[string, FieldType]> = [];
  for (const [key, field] of Object.entries(schema.fields)) {
    const fieldOverlay = resolved?.[key];
    if (field.requiresOverlay && (!resolved || fieldOverlay?.hidden)) continue;
    out.push([key, withOverlay(field, fieldOverlay)]);
  }
  return out;
}

function buildBaseline(fields: Array<[string, FieldType]>, raw: Record<string, unknown> | null): FormState {
  const next: FormState = {};
  for (const [key, field] of fields) {
    next[key] = coerceFromDoc(field, raw?.[key], raw ?? undefined);
  }
  return next;
}

function valuesEqual(a: FieldValue, b: FieldValue): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

type Check = { ok: true } | { ok: false; reason: string };

function checkNumber(
  field: { min?: number; max?: number; optional?: boolean },
  value: FieldValue,
): Check {
  if (value === undefined || value === '') {
    // An optional number's empty state is meaningful (no stored value), not a
    // validation failure — it must not block Save.
    return field.optional ? { ok: true } : { ok: false, reason: 'Required' };
  }
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return { ok: false, reason: 'Must be a number' };
  }
  if (typeof field.min === 'number' && value < field.min) {
    return { ok: false, reason: `Must be ≥ ${field.min}` };
  }
  if (typeof field.max === 'number' && value > field.max) {
    return { ok: false, reason: `Must be ≤ ${field.max}` };
  }
  return { ok: true };
}

function valueIsValid(field: FieldType, value: FieldValue): Check {
  switch (field.type) {
    case 'number':
      return checkNumber(field, value);
    case 'string': {
      const v = typeof value === 'string' ? value.trim() : '';
      if (field.required && !v) return { ok: false, reason: 'Required' };
      if (field.maxLength && v.length > field.maxLength) {
        return { ok: false, reason: `At most ${field.maxLength} characters (${v.length})` };
      }
      if (field.url && v && !/^https?:\/\/\S+$/i.test(v)) {
        return { ok: false, reason: 'Must start with http:// or https://' };
      }
      return { ok: true };
    }
    case 'date': {
      const v = typeof value === 'string' ? value : '';
      return !v || isIsoDate(v) ? { ok: true } : { ok: false, reason: 'Must be a date (YYYY-MM-DD)' };
    }
    case 'deep-link': {
      const err = buildDeepLink(toParsedDeepLink(value)).error;
      return err ? { ok: false, reason: err } : { ok: true };
    }
    case 'image': {
      const url = imageValue(value).url.trim();
      return !url || /^https?:\/\/\S+$/i.test(url)
        ? { ok: true }
        : { ok: false, reason: 'Image URL must start with http:// or https://' };
    }
    case 'cap':
      return checkCapValue(value as CapValue);
    case 'object': {
      const obj = (value as Record<string, FieldValue> | undefined) ?? {};
      for (const [k, sub] of Object.entries(field.fields)) {
        const c = valueIsValid(sub, obj[k]);
        if (!c.ok) return { ok: false, reason: `${sub.label}: ${c.reason}` };
      }
      const crossField = field.validate?.(storedObject(field, obj));
      if (crossField) return { ok: false, reason: crossField };
      return { ok: true };
    }
    case 'object-array': {
      const arr = Array.isArray(value) ? (value as Array<Record<string, FieldValue>>) : [];
      for (let i = 0; i < arr.length; i++) {
        for (const [k, sub] of Object.entries(field.itemFields)) {
          const v = arr[i]?.[k];
          if (sub.type === 'string') {
            if (typeof v !== 'string' || v.trim() === '') {
              return { ok: false, reason: `Row ${i + 1} — ${sub.label} is required` };
            }
          } else {
            const c = checkNumber(sub, v);
            if (!c.ok) return { ok: false, reason: `Row ${i + 1} — ${sub.label}: ${c.reason}` };
          }
        }
      }
      return { ok: true };
    }
    default:
      return { ok: true };
  }
}

function formatReadonly(value: FieldValue): string {
  if (value === undefined || value === null) return '—';
  if (Array.isArray(value)) return value.length ? value.map((v) => String(v)).join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// ── Panel ────────────────────────────────────────────────────────────────────

export function SchemaConfigPanel({ schema }: SchemaConfigPanelProps) {
  const { isAdmin, loading: adminLoading } = useIsRtdbAdmin();
  // Only admins can write to /configuration/* (RTDB rules). Whole-panel readonly
  // schemas are also locked. Fail safe: treat "still loading" as locked.
  const canEdit = isAdmin && !adminLoading && !schema.readonly;
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<SchemaOverlay | null>(null);
  // The document exactly as stored, kept alongside the parsed form for the
  // `showLiveValue` block. `null` is a genuinely different fact from an empty
  // object — it means nothing has ever been written here and the backend is on
  // its shipped defaults — and no input can express that difference.
  const [liveValue, setLiveValue] = useState<Record<string, unknown> | null>(null);
  const [baseline, setBaseline] = useState<FormState>({});
  const [form, setForm] = useState<FormState>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Bumped on every load/save so child inputs (number drafts, password reveal)
  // reset to the canonical value without clobbering in-progress edits.
  const [formNonce, setFormNonce] = useState(0);
  // Bumped to re-read the node (after the seed button writes it).
  const [reloadNonce, setReloadNonce] = useState(0);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setLoadError(null);
    // The overlay decides which fields exist and what an empty one falls back
    // to, so the baseline cannot be built until both have settled — resolving
    // them separately would render the form once with the wrong defaults and
    // then visibly correct itself. `resolveSchemaOverlay` never rejects, so
    // only the document read can fail the panel.
    Promise.all([
      fetchConfig(schema),
      schema.defaultsSource ? resolveSchemaOverlay(schema.defaultsSource) : Promise.resolve(null),
    ])
      .then(([raw, resolvedOverlay]) => {
        if (cancelled) return;
        const next = buildBaseline(effectiveFields(schema, resolvedOverlay), raw);
        setOverlay(resolvedOverlay);
        setLiveValue(raw);
        setBaseline(next);
        setForm(next);
        setFormNonce((n) => n + 1);
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load config');
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [schema, reloadNonce]);

  const handleSeed = async () => {
    if (!schema.seed || !canEdit) return;
    setSeeding(true);
    try {
      const created = await schema.seed.run();
      toast[created ? 'success' : 'error'](
        created
          ? `${schema.title}: created with defaults`
          : `${schema.title} already exists — nothing written`,
      );
      setReloadNonce((n) => n + 1);
    } catch (err) {
      toast.error(`${schema.title}: ${err instanceof Error ? err.message : 'seed failed'}`);
    } finally {
      setSeeding(false);
    }
  };

  const fieldEntries = useMemo(() => effectiveFields(schema, overlay), [schema, overlay]);

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    for (const [key, field] of fieldEntries) {
      // Read-only fields can't be fixed by the admin, so never let them gate Save.
      if (field.type === 'readonly' || field.readonly) continue;
      const check = valueIsValid(field, form[key]);
      if (!check.ok) errors[key] = check.reason;
    }
    return errors;
  }, [fieldEntries, form]);

  const dirtyKeys = useMemo(() => {
    return fieldEntries
      .filter(([key]) => !valuesEqual(form[key], baseline[key]))
      .map(([key]) => key);
  }, [fieldEntries, form, baseline]);

  const isDirty = dirtyKeys.length > 0;
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const handleSave = async () => {
    if (!canEdit || !isDirty || hasErrors) return;
    setSaving(true);
    try {
      const patch: Record<string, FieldValue> = {};
      const fieldByKey = new Map(fieldEntries);
      for (const key of dirtyKeys) {
        const field = fieldByKey.get(key);
        if (field) Object.assign(patch, storedEntries(key, field, form[key]));
      }
      await writeConfig(schema, patch);
      setBaseline(form);
      setSavedAt(Date.now());
      setFormNonce((n) => n + 1);
      toast.success(schema.savedMessage?.(patch) ?? `Saved ${schema.title}`);
      if (schema.showLiveValue) {
        // Re-read rather than merging the patch in: the point of this block is
        // to show what is actually stored, and only the node itself can report
        // the keys this panel does not render.
        // A failed re-read leaves the previous value on screen. Showing
        // "node absent" because a READ failed would assert the save was lost.
        void fetchConfig(schema)
          .then(setLiveValue)
          .catch(() => undefined);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(`${schema.title}: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-gray-200 rounded-lg p-4 bg-white">
      <header className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">{schema.title}</h3>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {schema.database === 'firestore' ? 'Firestore' : 'RTDB'} · {schema.path}
          </code>
          {schema.readonly && (
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              read-only
            </span>
          )}
        </div>
        {schema.description && (
          <p className="text-sm text-gray-600 mt-2">{schema.description}</p>
        )}
        {adminLoading && <p className="text-xs text-gray-400 mt-2">Checking permissions…</p>}
        {!adminLoading && !isAdmin && !schema.readonly && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
            Only admins can save changes to live settings.
          </p>
        )}
      </header>

      {loadState === 'loading' && <p className="text-sm text-gray-400">Loading…</p>}

      {loadState === 'error' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{loadError ?? 'Failed to load.'}</p>
        </div>
      )}

      {loadState === 'ready' && (
        <>
          {schema.seed && liveValue === null && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
              <p className="text-xs text-blue-900">
                Not set up yet — the node is absent, so the app runs on its bundled
                defaults (shown below). Create it to make those values explicit.
              </p>
              {canEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void handleSeed()}
                  loading={seeding}
                  disabled={seeding || isDirty}
                  title={isDirty ? 'Save or discard your edits first' : undefined}
                >
                  {schema.seed.label}
                </Button>
              )}
            </div>
          )}
          <div className="space-y-4">
            {fieldEntries.map(([key, field]) => (
              <FieldRow
                key={key}
                fieldKey={key}
                field={field}
                value={form[key]}
                error={fieldErrors[key]}
                readOnly={!canEdit || field.readonly === true}
                resetKey={formNonce}
                onChange={(next) => setForm((prev) => ({ ...prev, [key]: next }))}
              />
            ))}
          </div>

          {schema.showLiveValue && (
            <details className="mt-4 rounded border border-gray-200 bg-gray-50">
              <summary className="cursor-pointer select-none px-2.5 py-1.5 text-xs font-medium text-gray-600">
                Live value in {schema.database === 'firestore' ? 'Firestore' : 'RTDB'}
              </summary>
              <div className="border-t border-gray-200 px-2.5 py-2">
                {liveValue === null ? (
                  <p className="text-xs text-gray-500">
                    Node absent — nothing has been written here, so every value
                    above is the shipped default rather than a stored one.
                  </p>
                ) : (
                  <pre className="text-xs text-gray-700 overflow-x-auto whitespace-pre">
                    {JSON.stringify(liveValue, null, 2)}
                  </pre>
                )}
              </div>
            </details>
          )}

          {schema.warning && canEdit && (
            <p className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-2">
              {schema.warning}
            </p>
          )}

          {overlay?.status === 'unavailable' && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Backend defaults unavailable ({overlay.reason}). Fields whose shipped
              default and permitted range come from the backend are hidden rather
              than shown unsourced. The settings above read and write live values
              as usual, though where a key is absent from the node they fall back
              to off, which may not be the shipped default.
            </p>
          )}

          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              {!canEdit && !adminLoading && (
                <span className="text-amber-700">
                  {schema.readonly
                    ? 'Read-only reference — not editable.'
                    : 'Read-only — admin access required to edit.'}
                </span>
              )}
              {canEdit && hasErrors && (
                <span className="text-red-600">Fix errors before saving.</span>
              )}
              {canEdit && !hasErrors && isDirty && (
                <span>
                  {dirtyKeys.length} unsaved change
                  {dirtyKeys.length === 1 ? '' : 's'}.
                </span>
              )}
              {canEdit && !hasErrors && !isDirty && savedAt && (
                <span className="text-green-700">
                  Saved {new Date(savedAt).toLocaleTimeString()}.
                </span>
              )}
            </div>
            {canEdit && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                loading={saving}
                disabled={!isDirty || hasErrors || saving}
              >
                Save
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

// ── Field row + inputs ───────────────────────────────────────────────────────

interface FieldRowProps {
  fieldKey: string;
  field: FieldType;
  value: FieldValue;
  error?: string;
  readOnly?: boolean;
  resetKey: number;
  onChange: (next: FieldValue) => void;
}

function FieldRow({
  fieldKey,
  field,
  value,
  error,
  readOnly = false,
  resetKey,
  onChange,
}: FieldRowProps) {
  const labelId = `cfg-${fieldKey}`;
  const localCheck = valueIsValid(field, value);
  const effectiveError = error ?? (localCheck.ok ? undefined : localCheck.reason);
  const invalid = !!effectiveError;
  const showErrorText =
    invalid && field.type !== 'object' && field.type !== 'object-array';
  const inputBorder = invalid
    ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500';
  const disabledClasses = readOnly ? 'opacity-60 cursor-not-allowed bg-gray-50' : '';

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <label htmlFor={labelId} className="block text-sm font-medium text-gray-900">
            {field.label}
            {readOnly && field.readonly && (
              <span className="ml-2 text-xs font-normal text-gray-400">(read only)</span>
            )}
          </label>
          <code className="text-xs text-gray-400">{fieldKey}</code>
        </div>
        <div className="shrink-0">
          {field.type === 'boolean' && (
            <button
              type="button"
              id={labelId}
              role="switch"
              aria-checked={value === true}
              disabled={readOnly}
              onClick={() => onChange(!(value === true))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed ${
                value === true ? 'bg-brand-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  value === true ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          )}
        </div>
      </div>

      {field.type === 'number' && (
        <div className="mt-2">
          <NumberInput
            value={value}
            min={field.min}
            max={field.max}
            placeholder={field.placeholder}
            optional={field.optional}
            step={field.step}
            disabled={readOnly}
            invalid={invalid}
            resetKey={resetKey}
            onChange={onChange}
          />
        </div>
      )}

      {field.type === 'string' && (
        <input
          id={labelId}
          type="text"
          disabled={readOnly}
          value={typeof value === 'string' ? value : ''}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`mt-2 block w-full text-sm rounded-lg py-1.5 px-2 border ${inputBorder} ${disabledClasses}`}
        />
      )}

      {field.type === 'date' && (
        <div className="mt-2 flex items-center gap-2">
          <input
            id={labelId}
            type="date"
            disabled={readOnly}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className={`block text-sm rounded-lg py-1.5 px-2 border ${inputBorder} ${disabledClasses}`}
          />
          {!readOnly && typeof value === 'string' && value !== '' && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 px-2 py-1 rounded border border-gray-300"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {field.type === 'deep-link' && (
        <div className="mt-2">
          <DeepLinkField
            value={toParsedDeepLink(value)}
            onChange={(next) => !readOnly && onChange(deepLinkState(next))}
            label={field.label}
            inputClass={`block w-full text-sm rounded-lg py-1.5 px-2 border border-gray-300 ${disabledClasses}`}
            labelClass="sr-only"
          />
        </div>
      )}

      {field.type === 'image' && (
        <ImageInput field={field} value={value} readOnly={readOnly} onChange={onChange} />
      )}

      {field.type === 'select' && (
        <select
          id={labelId}
          disabled={readOnly}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className={`mt-2 block w-full max-w-xs text-sm rounded-lg py-1.5 px-2 border bg-white ${inputBorder} ${disabledClasses}`}
        >
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {field.type === 'password' && (
        <PasswordInput
          value={value}
          canEdit={!readOnly}
          resetKey={resetKey}
          placeholder={field.placeholder}
          onChange={onChange}
        />
      )}

      {field.type === 'string-array' && (
        <StringArrayInput
          value={value}
          readOnly={readOnly}
          valueSource={field.valueSource}
          onChange={onChange}
        />
      )}

      {field.type === 'cap' && (
        <div className="mt-2">
          <CapValueInput
            value={value as CapValue}
            numberId={labelId}
            disabled={readOnly}
            invalid={invalid}
            resetKey={resetKey}
            onChange={onChange}
          />
        </div>
      )}

      {field.type === 'readonly' && (
        <div className="mt-2 inline-block min-w-[8rem] text-sm text-gray-700 bg-gray-50 rounded-lg py-1.5 px-2 border border-gray-200">
          {formatReadonly(value)}
          <span className="ml-2 text-xs text-gray-400">(read only)</span>
        </div>
      )}

      {field.type === 'object' && (
        <div className="mt-2 rounded-lg border border-gray-200 p-3 space-y-3 bg-gray-50/40">
          {(() => {
            const msg = field.validate?.(storedObject(field, value));
            return msg ? <p className="text-xs text-red-600">{msg}</p> : null;
          })()}
          {Object.entries(field.fields).map(([subKey, subField]) => (
            <FieldRow
              key={subKey}
              fieldKey={subKey}
              field={subField}
              value={(value as Record<string, FieldValue> | undefined)?.[subKey]}
              readOnly={readOnly || subField.readonly === true}
              resetKey={resetKey}
              onChange={(next) =>
                onChange({ ...((value as Record<string, FieldValue>) ?? {}), [subKey]: next })
              }
            />
          ))}
        </div>
      )}

      {field.type === 'object-array' && (
        <ObjectArrayInput
          value={value}
          itemFields={field.itemFields}
          readOnly={readOnly}
          resetKey={resetKey}
          onChange={onChange}
        />
      )}

      {field.help && <p className="mt-1.5 text-xs text-gray-500">{field.help}</p>}
      {showErrorText && <p className="mt-1 text-xs text-red-600">{effectiveError}</p>}
    </div>
  );
}

function NumberInput({
  value,
  min,
  max,
  placeholder,
  optional,
  step,
  disabled,
  invalid,
  resetKey,
  onChange,
}: {
  value: FieldValue;
  min?: number;
  max?: number;
  placeholder?: string;
  optional?: boolean;
  step?: number | 'any';
  disabled?: boolean;
  invalid?: boolean;
  resetKey: number;
  onChange: (next: FieldValue) => void;
}) {
  // Local draft so the user can clear / type freely; reset to the canonical
  // value on load/save (resetKey bump) rather than on every keystroke. The
  // draft is what keeps a half-typed "-" or "-0." on screen — those parse to
  // nothing, and without it a negative number could not be typed at all.
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    setDraft(null);
  }, [resetKey]);
  const shown = draft !== null ? draft : typeof value === 'number' ? String(value) : '';
  const border = invalid
    ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500';
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        inputMode={typeof step === 'number' && step < 1 ? 'decimal' : 'numeric'}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
        value={shown}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          if (raw === '') {
            onChange(undefined);
            return;
          }
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : undefined);
        }}
        className={`block w-full max-w-xs text-sm rounded-lg py-1.5 px-2 border ${border} ${
          disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''
        }`}
      />
      {/* Removing an override is the whole point of an optional field, so it
          gets a control rather than relying on the admin discovering that an
          emptied box means "fall back to the default". Clears the draft too —
          the input is draft-first, so dropping the value alone would leave the
          old text on screen. */}
      {optional && !disabled && shown !== '' && (
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onChange(undefined);
          }}
          className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700 px-2 py-1 rounded border border-gray-300"
        >
          Use default
        </button>
      )}
    </div>
  );
}

function PasswordInput({
  value,
  canEdit,
  resetKey,
  placeholder,
  onChange,
}: {
  value: FieldValue;
  canEdit: boolean;
  resetKey: number;
  placeholder?: string;
  onChange: (next: FieldValue) => void;
}) {
  // Masked + locked by default. "Reveal & Edit" unmasks AND (if permitted)
  // unlocks editing. Revealing alone never mutates the value, so it can't
  // accidentally persist. Re-masks on load/save (resetKey bump).
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    setRevealed(false);
  }, [resetKey]);
  const str = typeof value === 'string' ? value : '';
  const editable = revealed && canEdit;
  return (
    <div className="mt-2 flex items-center gap-2 max-w-md">
      <input
        type={revealed ? 'text' : 'password'}
        readOnly={!editable}
        value={str}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`block w-full text-sm rounded-lg py-1.5 px-2 border border-gray-300 ${
          editable ? 'focus:ring-brand-500 focus:border-brand-500' : 'bg-gray-50'
        }`}
      />
      <button
        type="button"
        onClick={() => setRevealed((r) => !r)}
        className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700 px-2 py-1 rounded border border-gray-300"
      >
        {revealed ? 'Hide' : canEdit ? 'Reveal & Edit' : 'Reveal'}
      </button>
    </div>
  );
}

function StringArrayInput({
  value,
  readOnly,
  valueSource,
  onChange,
}: {
  value: FieldValue;
  readOnly: boolean;
  valueSource?: ValueSource;
  onChange: (next: FieldValue) => void;
}) {
  const arr = Array.isArray(value) ? (value as string[]) : [];
  const { options, loading } = useValueSourceOptions(valueSource);
  const [text, setText] = useState('');

  const add = (v: string) => {
    const t = v.trim();
    if (!t || arr.includes(t)) return;
    onChange([...arr, t]);
  };
  const remove = (v: string) => onChange(arr.filter((x) => x !== v));
  const available = options ? options.filter((o) => !arr.includes(o)) : null;

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        {arr.length === 0 && <span className="text-xs text-gray-400">None</span>}
        {arr.map((item) => (
          <span
            key={item}
            className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5"
          >
            {item}
            {!readOnly && (
              <button
                type="button"
                aria-label={`Remove ${item}`}
                onClick={() => remove(item)}
                className="text-gray-400 hover:text-red-600 leading-none"
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!readOnly && (
        <div className="mt-2 flex items-center gap-2">
          {available ? (
            <select
              value=""
              disabled={loading}
              onChange={(e) => {
                if (e.target.value) add(e.target.value);
              }}
              className="text-sm rounded-lg py-1.5 px-2 border border-gray-300 bg-white max-w-xs"
            >
              <option value="">{loading ? 'Loading…' : `Add (${available.length} available)…`}</option>
              {available.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    add(text);
                    setText('');
                  }
                }}
                placeholder="Add value…"
                className="text-sm rounded-lg py-1.5 px-2 border border-gray-300 max-w-xs"
              />
              <button
                type="button"
                onClick={() => {
                  add(text);
                  setText('');
                }}
                className="text-xs font-medium text-brand-600 px-2 py-1 rounded border border-gray-300"
              >
                Add
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ObjectArrayInput({
  value,
  itemFields,
  readOnly,
  resetKey,
  onChange,
}: {
  value: FieldValue;
  itemFields: Record<string, FieldType>;
  readOnly: boolean;
  resetKey: number;
  onChange: (next: FieldValue) => void;
}) {
  const rows = Array.isArray(value) ? (value as Array<Record<string, FieldValue>>) : [];
  const fieldList = Object.entries(itemFields);

  const update = (i: number, k: string, v: FieldValue) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => {
    const blank: Record<string, FieldValue> = {};
    for (const [k, sub] of fieldList) blank[k] = sub.type === 'number' ? undefined : '';
    onChange([...rows, blank]);
  };

  return (
    <div className="mt-2 space-y-2">
      {rows.length === 0 && <p className="text-xs text-gray-400">None.</p>}
      {rows.map((row, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              {fieldList.map(([k, sub]) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-gray-600">{sub.label}</label>
                  {sub.type === 'number' ? (
                    <NumberInput
                      value={row[k]}
                      min={sub.min}
                      max={sub.max}
                      disabled={readOnly}
                      resetKey={resetKey}
                      onChange={(v) => update(i, k, v)}
                    />
                  ) : (
                    <input
                      type="text"
                      disabled={readOnly}
                      value={typeof row[k] === 'string' ? (row[k] as string) : ''}
                      placeholder={sub.type === 'string' ? sub.placeholder : undefined}
                      onChange={(e) => update(i, k, e.target.value)}
                      className={`block w-full text-sm rounded-lg py-1.5 px-2 border border-gray-300 ${
                        readOnly ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="shrink-0 text-xs text-red-600 hover:text-red-700 px-2 py-1"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          onClick={addRow}
          className="text-xs font-medium text-brand-600 px-2 py-1 rounded border border-gray-300"
        >
          + Add
        </button>
      )}
    </div>
  );
}

/**
 * The reusable `image` field: preview at the slot's crop, upload (downscaled and
 * size-checked like the Brand Banner Fallbacks uploader), paste a URL instead,
 * or remove. Uploads land immediately at `{storagePrefix}/{timestamp}.{ext}`; the
 * node only changes on Save. Replaced or removed bytes are left in Storage — a
 * small orphan beats deleting an object another save might still point at.
 */
function ImageInput({
  field,
  value,
  readOnly,
  onChange,
}: {
  field: ImageField;
  value: FieldValue;
  readOnly: boolean;
  onChange: (next: FieldValue) => void;
}) {
  const { url, storagePath } = imageValue(value);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const upload = async (file: File) => {
    setProgress(0);
    try {
      const name = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const res = await uploadPublicAdminImage(field.storagePrefix, name, file, {
        maxWidth: field.maxWidth,
        maxBytes: field.maxBytes,
        onProgress: setProgress,
      });
      onChange({ url: res.downloadUrl, storagePath: res.storagePath });
      toast.success(
        res.resized
          ? `Uploaded (downscaled from ${res.originalWidth}px) — Save to apply`
          : 'Uploaded — Save to apply',
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      // A storage/unauthorized here almost always means the prefix has no
      // storage.rules block yet — say so, rather than a bare 403.
      const hint = /unauthori[sz]ed|403|permission/i.test(msg)
        ? ` — check that storage.rules allows admin writes to ${field.storagePrefix}/`
        : '';
      toast.error(`${msg}${hint}`);
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {url ? (
        <div className="max-w-md">
          <BannerPreview
            key={url}
            url={url}
            alt={field.label}
            aspect={field.previewAspect}
            minRecommendedWidth={field.recommendedMinWidth}
          />
          <p className="mt-1 text-[11px] text-gray-400 break-all">
            {storagePath ? `Storage: ${storagePath}` : 'Pasted URL (not uploaded here)'}
          </p>
        </div>
      ) : (
        <p className="text-xs text-gray-400">No image.</p>
      )}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fileRef.current?.click()}
            loading={progress !== null}
            disabled={progress !== null}
          >
            {progress !== null ? `Uploading ${progress}%` : url ? 'Replace image' : 'Upload image'}
          </Button>
          {url && (
            <button
              type="button"
              onClick={() => onChange({ url: '', storagePath: '' })}
              className="text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded border border-gray-300"
            >
              Remove
            </button>
          )}
          <input
            type="url"
            value={storagePath ? '' : url}
            placeholder="…or paste an image URL"
            onChange={(e) => onChange({ url: e.target.value, storagePath: '' })}
            className="min-w-[16rem] flex-1 text-sm rounded-lg py-1.5 px-2 border border-gray-300"
          />
        </div>
      )}
    </div>
  );
}
